import { useState, useCallback } from "react";
import Navbar from "../home/components/Navbar";
import Footer from "../home/components/Footer";
import BibleControls from "./components/BibleControls";
import VerseDisplay from "./components/VerseDisplay";
import ReadingPlanView from "./components/ReadingPlanView";
import ReaderToolbar from "./components/ReaderToolbar";
import { useReaderSettings } from "../../hooks/useReaderSettings";

type Tab = "reader" | "plan";

export default function ScripturePage() {
  const [tab, setTab] = useState<Tab>("reader");
  const [translation, setTranslation] = useState("KJV");
  const [bookId, setBookId] = useState(40);
  const [chapter, setChapter] = useState(1);
  const [showControls, setShowControls] = useState(false);

  const { theme, setTheme, accentColor, setAccentColor } = useReaderSettings();
  const isDark = theme === "dark";

  const handleNavigate = useCallback((bId: number, ch: number) => {
    setBookId(bId);
    setChapter(ch);
    setTab("reader");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const toggleTheme = () => setTheme(isDark ? "light" : "dark");

  const tabs: { id: Tab; label: string; icon: string }[] = [
    { id: "reader", label: "Bible Reader", icon: "ri-book-open-line" },
    { id: "plan", label: "365 Day Track", icon: "ri-calendar-check-line" },
  ];

  const pageBg = isDark ? "bg-[#0a0a0a]" : "bg-[#f7f3ee]";
  const titleColor = isDark ? "text-white" : "text-[#1a1a1a]";
  const subtitleColor = isDark ? "text-[#555]" : "text-[#888]";
  const tabBarBg = isDark ? "bg-[#111] border-[#1e1e1e]" : "bg-[#ede8e1] border-[#ddd5c8]";
  const tabActive = isDark ? "bg-[#1e1e1e] text-white" : "bg-white text-[#1a1a1a] shadow-sm";
  const tabInactive = isDark ? "text-[#666] hover:text-white" : "text-[#999] hover:text-[#1a1a1a]";
  const contentBg = isDark
    ? "bg-[#0d0d0d] border-[#1a1a1a]"
    : "bg-white border-[#e8e0d4]";
  const borderColor = isDark ? "border-[#1a1a1a]" : "border-[#e8e0d4]";
  const prevNextColor = isDark
    ? "text-[#555] hover:text-white"
    : "text-[#aaa] hover:text-[#1a1a1a]";
  const chapterNumColor = isDark ? "text-[#333]" : "text-[#bbb]";

  return (
    <div className={`min-h-screen ${pageBg} transition-colors duration-300`}>
      <Navbar />

      <div className="max-w-6xl mx-auto px-4 md:px-6 pt-24 pb-20">
        {/* Page title */}
        <div className="mb-8">
          <h1 className={`text-2xl md:text-3xl font-bold mb-1 ${titleColor}`}>Scripture</h1>
          <p className={`text-sm ${subtitleColor}`}>
            Read, search, and plan your journey through the Bible
          </p>
        </div>

        {/* Tab switcher */}
        <div
          className={`flex items-center gap-1 border rounded-lg p-1 w-fit mb-6 flex-wrap ${tabBarBg}`}
        >
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap ${
                tab === t.id ? tabActive : tabInactive
              }`}
            >
              <i className={`${t.icon} text-xs`} />
              {t.label}
            </button>
          ))}
        </div>

        {/* Bible Reader */}
        {tab === "reader" && (
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Mobile controls toggle */}
            <div className="lg:hidden">
              <button
                onClick={() => setShowControls(!showControls)}
                className={`flex items-center gap-2 border text-sm px-4 py-2 rounded-lg w-full justify-between cursor-pointer ${
                  isDark
                    ? "bg-[#111] border-[#1e1e1e] text-white"
                    : "bg-white border-[#e0d8ce] text-[#1a1a1a]"
                }`}
              >
                <span>Change Book / Translation</span>
                <i
                  className={`ri-arrow-${showControls ? "up" : "down"}-s-line ${
                    isDark ? "text-[#555]" : "text-[#aaa]"
                  }`}
                />
              </button>
              {showControls && (
                <div className="mt-2">
                  <BibleControls
                    translation={translation}
                    bookId={bookId}
                    chapter={chapter}
                    accentColor={accentColor}
                    isDark={isDark}
                    onTranslationChange={setTranslation}
                    onBookChange={(id) => { setBookId(id); setShowControls(false); }}
                    onChapterChange={(ch) => { setChapter(ch); setShowControls(false); }}
                  />
                </div>
              )}
            </div>

            {/* Desktop sidebar */}
            <aside className="hidden lg:block w-72 flex-shrink-0">
              <div className="sticky top-20 h-[calc(100vh-6rem)] overflow-y-auto pr-1 scrollbar-hide">
                <BibleControls
                  translation={translation}
                  bookId={bookId}
                  chapter={chapter}
                  accentColor={accentColor}
                  isDark={isDark}
                  onTranslationChange={setTranslation}
                  onBookChange={setBookId}
                  onChapterChange={setChapter}
                />
              </div>
            </aside>

            {/* Verse content */}
            <main
              className={`flex-1 min-w-0 border rounded-xl p-6 md:p-8 transition-colors duration-300 ${contentBg}`}
            >
              <ReaderToolbar
                theme={theme}
                accentColor={accentColor}
                onThemeToggle={toggleTheme}
                onAccentChange={setAccentColor}
              />

              <VerseDisplay
                translation={translation}
                bookId={bookId}
                chapter={chapter}
                theme={theme}
                accentColor={accentColor}
              />

              {/* Chapter nav */}
              <div
                className={`flex items-center justify-between mt-10 pt-6 border-t ${borderColor}`}
              >
                <button
                  onClick={() => chapter > 1 && setChapter((c) => c - 1)}
                  disabled={chapter <= 1}
                  className={`flex items-center gap-1.5 text-sm disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap ${prevNextColor}`}
                >
                  <i className="ri-arrow-left-line" /> Previous
                </button>
                <span className={`text-xs ${chapterNumColor}`}>Chapter {chapter}</span>
                <button
                  onClick={() => setChapter((c) => c + 1)}
                  className={`flex items-center gap-1.5 text-sm transition-colors cursor-pointer whitespace-nowrap ${prevNextColor}`}
                >
                  Next <i className="ri-arrow-right-line" />
                </button>
              </div>
            </main>
          </div>
        )}

        {/* Reading Plan */}
        {tab === "plan" && <ReadingPlanView onNavigate={handleNavigate} />}
      </div>

      <Footer />
    </div>
  );
}
